import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  request,
  type Meeting,
  type MeetingActionStatus,
  type MeetingActionsResponse,
  type MeetingDetailResponse,
  type MeetingItem,
  type MeetingItemKind,
  type MeetingListResponse,
  type MeetingPeopleResponse,
  type MeetingPhase,
  type MeetingAskResponse,
  type MeetingSendResult,
  type MeetingSuggestResponse,
  type MeetingSummaryDraft,
} from './client';

/**
 * Meetings — one lifecycle, week after week. docs/api-contract-meetings.md.
 *
 * Everything here is on the generated shapes, so the build breaks where this
 * screen and the API disagree.
 *
 *   GET    /meetings/people                       meetings:read
 *   GET    /meetings/actions?owner=&state=        meetings:read
 *   GET    /meetings/?phase=&q=&page=             meetings:read
 *   POST   /meetings/                             meetings:update   audit meeting.created
 *   GET    /meetings/{id}                         meetings:read
 *   PATCH  /meetings/{id}                         meetings:update   audit meeting.updated
 *   POST   /meetings/{id}/phase                   meetings:update   audit meeting.phase_changed
 *   POST   /meetings/{id}/items                   meetings:update   audit meeting.item_added
 *   PATCH  /meetings/{id}/items/{itemId}          meetings:update   audit meeting.item_updated
 *   DELETE /meetings/{id}/items/{itemId}          meetings:update   audit meeting.item_removed
 *   POST   /meetings/{id}/agenda/suggest          meetings:update   audit meeting.ai_used — nothing saved
 *   POST   /meetings/{id}/summary/draft           meetings:update   audit meeting.ai_used — nothing saved
 *   POST   /meetings/{id}/ask                     meetings:read     audit meeting.ai_used — Ena answers, nothing saved
 *   POST   /meetings/{id}/send                    meetings:update   audit meeting.sent
 *
 * An API older than these routes answers 404 on the collection, which the
 * section keys its preview on — see `isEndpointMissing`.
 */

export const PHASE_LABEL: Record<MeetingPhase, string> = {
  preparing: 'Preparing',
  in_meeting: 'In meeting',
  done: 'Done',
};

export const KIND_LABEL: Record<MeetingItemKind, string> = {
  agenda: 'Agenda',
  note: 'Note',
  question: 'Question',
  takeaway: 'Takeaway',
  action: 'Action item',
};

export const STATUS_LABEL: Record<MeetingActionStatus, string> = {
  open: 'Open',
  done: 'Done',
  dropped: 'Dropped',
};

export interface MeetingFilters {
  phase?: MeetingPhase;
  q?: string;
  page?: number;
  pageSize?: number;
}

export const useMeetings = (filters: MeetingFilters, enabled = true) =>
  useQuery({
    queryKey: ['meetings', filters],
    queryFn: () => request<MeetingListResponse>('/meetings/', { query: { ...filters } }),
    placeholderData: (prev) => prev,
    enabled,
  });

export const useMeeting = (id: string | null) =>
  useQuery({
    queryKey: ['meeting', id],
    queryFn: () => request<MeetingDetailResponse>(`/meetings/${id}`),
    enabled: id !== null,
    // Two people typing in the same room see each other's lines.
    refetchInterval: (q) => (q.state.data?.meeting.phase === 'in_meeting' ? 15_000 : false),
  });

/** Active accounts, for the attendee and owner pickers. */
export const useMeetingPeople = (enabled = true) =>
  useQuery({
    queryKey: ['meeting-people'],
    queryFn: () => request<MeetingPeopleResponse>('/meetings/people'),
    enabled,
    staleTime: 60_000,
  });

export interface ActionFilters {
  owner: 'me' | 'all';
  state: 'open' | 'late' | 'closed';
}

/** Open action items across every meeting, due soonest first. */
export const useOpenActions = (filters: ActionFilters, enabled = true) =>
  useQuery({
    queryKey: ['meeting-actions', filters],
    queryFn: () => request<MeetingActionsResponse>('/meetings/actions', { query: { ...filters } }),
    placeholderData: (prev) => prev,
    enabled,
  });

/** Everything that changes a meeting invalidates the same three views. */
function useMeetingWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meetings'] });
      void qc.invalidateQueries({ queryKey: ['meeting'] });
      void qc.invalidateQueries({ queryKey: ['meeting-actions'] });
    },
  });
}

export interface CreateMeetingBody {
  title: string;
  scheduledAt?: string | null;
  attendeeIds?: string[];
  continuesFromId?: string | null;
}

export const useCreateMeeting = () =>
  useMeetingWrite((body: CreateMeetingBody) =>
    request<MeetingDetailResponse>('/meetings/', { method: 'POST', body }),
  );

export interface UpdateMeetingBody {
  title?: string;
  scheduledAt?: string | null;
  attendeeIds?: string[];
  summary?: string | null;
  summarySource?: 'manual' | 'ai';
}

export const useUpdateMeeting = () =>
  useMeetingWrite(({ id, ...body }: UpdateMeetingBody & { id: string }) =>
    request<{ meeting: Meeting }>(`/meetings/${id}`, { method: 'PATCH', body }),
  );

export const useSetPhase = () =>
  useMeetingWrite(({ id, phase }: { id: string; phase: 'in_meeting' | 'done' }) =>
    request<{ meeting: Meeting }>(`/meetings/${id}/phase`, { method: 'POST', body: { phase } }),
  );

export interface AddItemBody {
  kind: MeetingItemKind;
  body: string;
  ownerId?: string | null;
  dueOn?: string | null;
  originItemId?: string;
  source?: 'manual' | 'ai';
}

export const useAddItem = () =>
  useMeetingWrite(({ id, ...body }: AddItemBody & { id: string }) =>
    request<{ item: MeetingItem }>(`/meetings/${id}/items`, { method: 'POST', body }),
  );

export interface ItemPatch {
  body?: string;
  ownerId?: string | null;
  dueOn?: string | null;
  status?: MeetingActionStatus;
  covered?: boolean;
  answered?: boolean;
}

export const useUpdateItem = () =>
  useMeetingWrite(({ id, itemId, ...patch }: ItemPatch & { id: string; itemId: string }) =>
    request<{ item: MeetingItem }>(`/meetings/${id}/items/${itemId}`, { method: 'PATCH', body: patch }),
  );

export const useRemoveItem = () =>
  useMeetingWrite(({ id, itemId }: { id: string; itemId: string }) =>
    request<{ ok: boolean }>(`/meetings/${id}/items/${itemId}`, { method: 'DELETE' }),
  );

/*
  The two AI helpers save nothing, so there is nothing to invalidate: what
  comes back sits on screen until a person adds a line or saves the draft.
*/
export const useSuggestAgenda = () =>
  useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      request<MeetingSuggestResponse>(`/meetings/${id}/agenda/suggest`, { method: 'POST', body: { notes } }),
  });

export const useDraftSummary = () =>
  useMutation({
    mutationFn: ({ id }: { id: string }) =>
      request<MeetingSummaryDraft>(`/meetings/${id}/summary/draft`, { method: 'POST', body: {} }),
  });

export const useSendSummary = () =>
  useMeetingWrite(({ id }: { id: string }) =>
    request<MeetingSendResult>(`/meetings/${id}/send`, { method: 'POST', body: {} }),
  );

export interface AskTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** A question for Ena about one meeting, with the conversation so far. Nothing is saved, so nothing to invalidate. */
export const useAskEna = () =>
  useMutation({
    mutationFn: ({ id, question, history }: { id: string; question: string; history: AskTurn[] }) =>
      request<MeetingAskResponse>(`/meetings/${id}/ask`, { method: 'POST', body: { question, history } }),
  });

/**
 * The routes are not there.
 *
 * Only the collection route decides it: a 404 from `/meetings/{id}` is a
 * missing meeting, not a missing API.
 */
export const isEndpointMissing = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 404;
